import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute } from '@tanstack/react-router'

import { AdminSidebarContainer } from '@/components/app/admin-sidebar-container'
import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import { AdminPlansWorkspace } from '@/components/screens/admin-plans-workspace'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'

export const Route = createFileRoute('/admin/plans')({
  component: Page,
})

function Page() {
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebarContainer />
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 xl:px-10">
        <PlansContainer />
      </main>
    </div>
  )
}

function PlansContainer() {
  const client = api.useClient()
  const plansQuery = api.useFetchAdminPlansQuery()

  // 未取得だがエラーでもない（読み込み中）場合は、画面ごと差し替えずに DataTable の skeleton に任せる
  // （画面まるごとの CenteredLoading をやめる。2026/08 レビューFB対応）
  if (isNull(plansQuery.data) && isSome(plansQuery.error)) {
    return (
      <GraphqlErrorPanel
        layout="section"
        error={plansQuery.error}
        subject="プラン一覧"
        onRetry={() => {
          void plansQuery.refetch()
        }}
      />
    )
  }

  const runMutation = async (
    mutate: () => Promise<unknown>,
    failureMessage: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await mutate()
    } catch (error) {
      return { ok: false, message: userFacingErrorMessage(error, failureMessage) }
    }
    try {
      await plansQuery.refetch()
    } catch {
      // 再取得の失敗を「操作の失敗」として報告しない
    }
    return { ok: true }
  }

  return (
    <AdminPlansWorkspace
      plans={plansQuery.data?.adminPlans ?? []}
      loading={plansQuery.loading}
      onCreate={async (input) =>
        await runMutation(async () => await client.createAdminPlan({ plan: input }), 'プランの作成に失敗しました')
      }
      onUpdate={async (input) =>
        await runMutation(async () => await client.updateAdminPlan({ plan: input }), 'プランの更新に失敗しました')
      }
      onDelete={async (id) =>
        await runMutation(async () => await client.deleteAdminPlan({ id }), 'プランの削除に失敗しました')
      }
    />
  )
}
